import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { api } from '../api';
import { useNavigate, Link } from 'react-router-dom';
import { authHeader, clearAuth } from '../auth';
import { Crown, User, FileText, MessageCircle, LogOut, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f3f4f6;
  padding: 2rem 1rem;
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  font-size: 1.8rem;
  font-weight: 800;
  color: #111827;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border-radius: 999px;
  padding: 0.35rem 0.8rem;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${props => (props.premium ? '#eef2ff' : '#e5e7eb')};
  color: ${props => (props.premium ? '#4f46e5' : '#374151')};
`;

const Banner = styled.div`
  background: #fff7ed;
  border: 1px solid #fdba74;
  color: #9a3412;
  padding: 0.9rem 1.1rem;
  border-radius: 14px;
  margin-bottom: 1.25rem;
  font-size: 0.9rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 10px 25px rgba(15,23,42,0.08);
`;

const CardTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CardText = styled.p`
  font-size: 0.9rem;
  color: #4b5563;
  margin-bottom: 0.75rem;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const Chip = styled.span`
  border-radius: 999px;
  padding: 0.3rem 0.7rem;
  background: #f3f4f6;
  font-size: 0.8rem;
  color: #374151;
`;

const LinkButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.85rem;
  color: #4f46e5;
  text-decoration: none;
  margin-top: 0.5rem;
`;

const LogoutButton = styled.button`
  border: none;
  border-radius: 999px;
  padding: 0.5rem 0.9rem;
  background: #fee2e2;
  color: #b91c1c;
  font-size: 0.8rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  cursor: pointer;
`;

const PremiumList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0.75rem 0 0;
  display: grid;
  gap: 0.4rem;
  font-size: 0.9rem;
  color: #374151;
`;

const Dashboard = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await api.get('/api/auth/me', { headers: authHeader() });
        setProfile(res.data);
      } catch (err) {
        clearAuth();
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [navigate]);

  const handleLogout = () => {
    clearAuth();
    toast.success('Çıkış yapıldı');
    navigate('/');
  };

  if (loading) {
    return (
      <PageContainer>
        <Container>
          <p>Yükleniyor...</p>
        </Container>
      </PageContainer>
    );
  }

  if (!profile) return null;

  const now = new Date();
  const until = profile.premium_until ? new Date(profile.premium_until) : null;
  const isPremium = !!(until && until > now);
  const daysLeft = until ? Math.ceil((until.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)) : null;
  const expiringSoon = isPremium && daysLeft !== null && daysLeft <= 3;

  return (
    <PageContainer>
      <Container>
        <HeaderRow>
          <div>
            <Title>Merhaba, {profile.name || profile.email}</Title>
            <Badge premium={isPremium}>
              {isPremium ? (
                <>
                  <Crown size={14} /> Premium Üye
                </>
              ) : (
                <>
                  <User size={14} /> Temel Uyelik
                </>
              )}
            </Badge>
          </div>
          <LogoutButton onClick={handleLogout}>
            <LogOut size={14} />
            Çıkış Yap
          </LogoutButton>
        </HeaderRow>

        {expiringSoon && (
          <Banner>
            Premium süren yakında bitiyor. Tüm araçlara erişmeye devam etmek için tekrar satın alabilirsin.
          </Banner>
        )}

        <Grid>
          <Card>
            <CardTitle>
              <User size={18} />
              Profil Bilgileri
            </CardTitle>
            <CardText><strong>Email:</strong> {profile.email}</CardText>
            <CardText><strong>Plan:</strong> {isPremium ? 'Premium' : 'Temel'}</CardText>
            {until && (
              <CardText>
                <strong>Premium bitiş tarihi:</strong>{' '}
                {until.toLocaleString()}
              </CardText>
            )}
          </Card>

          <Card>
            <CardTitle>
              <FileText size={18} />
              CV Araçları
            </CardTitle>
            <CardText>CV'ni geliştirmek ve ön çıkarmak için araçlar:</CardText>
            <ChipRow>
              <Chip>CV Analizi</Chip>
              <Chip>CV Oluşturma</Chip>
              <Chip>İlan Uyumu</Chip>
            </ChipRow>
            <LinkButton to="/cv-analyzer">
              CV Analizine Git <ArrowRight size={14} />
            </LinkButton>
          </Card>

          <Card>
            <CardTitle>
              <MessageCircle size={18} />
              Kariyer & Mülakat
            </CardTitle>
          
            <CardText>Kariyer koçluğu ve mülakat pratikleriyle hazırlığını güçlendir.</CardText>
            <ChipRow>
              <Chip>Mülakat Simülasyonu</Chip>
              <Chip>Kariyer Kocu</Chip>
            </ChipRow>
            <LinkButton to="/interview-simulator">
              Mülakat Simülasyonuna Git <ArrowRight size={14} />
            </LinkButton>
          </Card>

          <Card>
            <CardTitle>
              <Crown size={18} />
              Premium Üyelik
            </CardTitle>
            {isPremium ? (
              <>
                <CardText>Premium aktif</CardText>
                <PremiumList>
                  <li>30 gün boyunca tüm araçlar sınırsız</li>
                  <li>Otomatik yenileme yok</li>
                  <li>Abonelik yok</li>
                  <li>Eğer istersen süresi bitince tekrar satın alabilirsiniz</li>
                </PremiumList>
              </>
            ) : (
              <>
                <CardText>
                  Premium Pass ile tüm araçları 30 gün boyunca sınırsız kullanabilirsin.
                </CardText>
                <LinkButton to="/pricing">
                  Premium Pass Satın Al <ArrowRight size={14} />
                </LinkButton>
              </>
            )}
          </Card>
        </Grid>
      </Container>
    </PageContainer>
  );
};

export default Dashboard;
